import i18n from "@/i18n.ts";
import { ApiIdTitle } from "@/types/common.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowBlockDelayReference,
  ApiWorkflowBlockTypes,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlockEdgeShort } from "@/types/workflows/workflow.types.ts";

export enum ApiWorkflowExecutionStatus {
  pending = "pending",
  running = "running",
  completed = "completed",
  failed = "failed",
  cancelled = "cancelled",
}

export const getApiWorkflowExecutionStatusTitle = (type: ApiWorkflowExecutionStatus): string => {
  switch (type) {
    case ApiWorkflowExecutionStatus.pending:
      return i18n.t("common:status_pending_launch");
    case ApiWorkflowExecutionStatus.running:
      return i18n.t("common:status_running");
    case ApiWorkflowExecutionStatus.completed:
      return i18n.t("common:status_completed");
    case ApiWorkflowExecutionStatus.failed:
      return i18n.t("common:status_failed");
    case ApiWorkflowExecutionStatus.cancelled:
      return i18n.t("common:status_cancelled");
  }
};

export enum ApiWorkflowExecutionBlockStatus {
  pending = "pending",
  running = "running",
  completed = "completed",
  waiting_for_external = "waiting_for_external",
  scheduled = "scheduled",
  skipped_by_condition = "skipped_by_condition",
  failed = "failed",
  cancelled = "cancelled",
}

export const getApiWorkflowExecutionBlockStatusTitle = (
  status: ApiWorkflowExecutionBlockStatus
): string => {
  switch (status) {
    case ApiWorkflowExecutionBlockStatus.pending:
      return i18n.t("common:status_pending_launch");
    case ApiWorkflowExecutionBlockStatus.running:
      return i18n.t("common:status_running");
    case ApiWorkflowExecutionBlockStatus.waiting_for_external:
      return i18n.t("common:waiting_for_external");
    case ApiWorkflowExecutionBlockStatus.scheduled:
      return i18n.t("common:status_pending_launch");
    case ApiWorkflowExecutionBlockStatus.skipped_by_condition:
      return i18n.t("common:skipped_by_condition");
    case ApiWorkflowExecutionBlockStatus.completed:
      return i18n.t("common:status_completed");
    case ApiWorkflowExecutionBlockStatus.failed:
      return i18n.t("common:status_failed");
    case ApiWorkflowExecutionBlockStatus.cancelled:
      return i18n.t("common:status_cancelled");
  }
};

export type ApiWorkflowBlockCancelReason =
  | "source_event_cancelled"
  | "block_deleted"
  | "candidate_state_changed"
  | "esign_envelope_cancelled";

export interface ApiWorkflowExecutionBlockDetail {
  id: number;
  workflow_block_id: number;
  status: ApiWorkflowExecutionBlockStatus;
  block_type: ApiWorkflowBlockTypes;
  action_type: ApiWorkflowBlockActionTypes;
  title: string | null;
  datetime_started_at: Date | null;
  datetime_completed_at: Date | null;
  datetime_scheduled_at: Date | null;
  delay_reference: ApiWorkflowBlockDelayReference;
  datetime_effective_due_at: Date | null;
  cancel_reason: ApiWorkflowBlockCancelReason | null;
  error_message: string | null;
}

export interface ApiWorkflowExecutionCandidate {
  id: number;
  applicant_id: number;
  first_name: string;
  last_name: string;
  middle_name: string;
}

export interface ApiWorkflowExecutionACL {
  can_cancel: boolean;
}

export interface ApiWorkflowExecutionTreeBlock {
  id: number;
  block_type: ApiWorkflowBlockTypes;
  action_type: ApiWorkflowBlockActionTypes;
  title: string | null;
  subtitle: string | null;
}

export interface ApiWorkflowExecutionDetail {
  id: number;
  workflow_id: number;
  workflow_title: string;
  workflow_public_title: string | null;
  status: ApiWorkflowExecutionStatus;
  datetime_created_at: Date | null;
  datetime_started_at: Date | null;
  datetime_completed_at: Date | null;
  error_message: string | null;
  blocks: ApiWorkflowExecutionBlockDetail[];
  workflow_blocks: ApiWorkflowExecutionTreeBlock[];
  edges: ApiWorkflowBlockEdgeShort[];
  employee: ApiEmployee | null;
  candidate: ApiWorkflowExecutionCandidate | null;
  job: ApiIdTitle | null;
  acl: ApiWorkflowExecutionACL;
}

export interface ApiWorkflowExecutionMeeting {
  execution_block_id: number;
  workflow_block_id: number;
  event_id: number;
}

export interface ApiWorkflowExecutionListItem {
  id: number;
  title: string;
  status: ApiWorkflowExecutionStatus;
  datetime_created_at: Date;
  datetime_not_before_at: Date | null;
  employee: ApiEmployee | null;
  candidate: ApiWorkflowExecutionCandidate | null;
  job: ApiIdTitle | null;
}

export interface ApiWorkflowExecutionFilter extends ApiFilterBase {
  workflow_ids?: number[];
  employee_ids?: number[];
  statuses?: ApiWorkflowExecutionStatus[];
  date_from?: Date;
  date_to?: Date;
}
